import { directorApi } from '../director/directorApi'
import { BFF_SERVICE } from '../../utils/constants'
import storage from '../../utils/storageService'

// «Оқушылардан опрос»: /api/surveys/*. Поля = studentSurvey.proto, 0 / "" / -1 -> null в bff.

export type SurveyKind = 'RATING' | 'CHOICE' | 'TEXT' | 'NPS'
export type SurveyType = 'MENTOR' | 'SUBJECT_HEAD' | 'TEACHER' | 'NPS'
export const SURVEY_TYPES: SurveyType[] = ['MENTOR', 'SUBJECT_HEAD', 'TEACHER', 'NPS']

export interface SurveyOption {
    textKz: string | null
    textRu: string | null
}

export interface SurveyQuestion {
    id?: number | null
    position?: number
    kind: SurveyKind
    textKz: string | null
    textRu: string | null
    options: SurveyOption[]
    required: boolean
}

export interface Survey {
    id: number
    titleKz: string | null
    titleRu: string | null
    courseId: number | null
    subjectId: number | null
    monthNumber: number | null
    openDate: string | null
    closeDate: string | null
    active: boolean
    open: boolean
    isDefault: boolean
    responseCount: number
    locked: boolean
    courseTitleKz: string | null
    courseTitleRu: string | null
    subjectTitleKz: string | null
    subjectTitleRu: string | null
    questions: SurveyQuestion[]
    groupIds: number[]
    type: SurveyType | null
    // TEACHER: KZ | RU, null - оба
    language: string | null
    subjectHeadUserId: number | null
    subjectHeadFirstName: string | null
    subjectHeadLastName: string | null
    reopenedAt: string | null
    finished: boolean
    // TEACHER: кого оценивают; null - старые опросы (учитель группы)
    teacherUserId: number | null
    teacherFirstName: string | null
    teacherLastName: string | null
}

export interface SurveyBody {
    titleKz: string
    titleRu: string
    courseId: number | null
    subjectId: number | null
    monthNumber: number | null
    openDate: string | null
    closeDate: string | null
    questions: SurveyQuestion[]
    // пусто - все группы потока
    groupIds: number[]
    type: SurveyType
    language: string | null
    subjectHeadUserId: number | null
    teacherUserId: number | null
}

export interface SurveyOptionCourse {
    courseId: number
    titleKz: string | null
    titleRu: string | null
    subjects: {
        courseSubjectId: number
        subjectId: number
        titleKz: string | null
        titleRu: string | null
        groups: { id: number; title: string | null }[]
    }[]
}

// пән басшы для опроса SUBJECT_HEAD: строка subject_head_subject
export interface SurveyOptionHead {
    userId: number
    subjectId: number
    language: string | null
    firstName: string | null
    lastName: string | null
    subjectTitleKz: string | null
    subjectTitleRu: string | null
}

export interface SurveyOptionTeacher {
    userId: number
    firstName: string | null
    lastName: string | null
}

export interface SurveyOptions {
    courses: SurveyOptionCourse[]
    heads: SurveyOptionHead[]
    teachers: SurveyOptionTeacher[]
}

export interface SurveyQuestionResult {
    questionId: number
    kind: SurveyKind
    average: number | null
    answered: number
    choiceCounts: number[]
    texts: string[]
    // NPS: % 9-10 минус % 0-6
    nps: number | null
}

export interface SurveyAnswerValue {
    questionId: number
    rating: number | null
    // номер варианта с 1
    choice: number | null
    text: string | null
    nps: number | null
}

// «Жауап №number»; ученик виден модератору, сервис басшы и пән басшы
export interface SurveyAnswerRow {
    number: number
    answers: SurveyAnswerValue[]
    studentUserId: number | null
    firstName: string | null
    lastName: string | null
}

export interface SurveyMentorResult {
    // NPS - null
    mentorUserId: number | null
    firstName: string | null
    lastName: string | null
    courseSubjectId: number
    subjectTitleKz: string | null
    subjectTitleRu: string | null
    responses: number
    students: number
    percent: number | null
    appliedPercent: number | null
    questions: SurveyQuestionResult[]
    answerRows: SurveyAnswerRow[]
}

export interface SurveyResults {
    survey: Survey
    canApply: boolean
    mentors: SurveyMentorResult[]
}

// анкета для заполнения: куратор заполняет опрос о пән басшы (/mentor/surveys)
export interface MySurveyItem {
    surveyId: number
    titleKz: string | null
    titleRu: string | null
    mentorUserId: number | null
    mentorFirstName: string | null
    mentorLastName: string | null
    courseSubjectId: number | null
    subjectTitleKz: string | null
    subjectTitleRu: string | null
    closeDate: string | null
    answered: boolean
    type: SurveyType | null
}

export interface MySurvey {
    item: MySurveyItem
    questions: SurveyQuestion[]
}

// ментор: кто из его учеников прошёл открытый опрос, без ответов
export interface MentorSurveyStatus {
    surveyId: number
    titleKz: string | null
    titleRu: string | null
    type: SurveyType | null
    closeDate: string | null
    students: { studentUserId: number; firstName: string | null; lastName: string | null; answered: boolean }[]
}

export interface SurveyAnswerBody {
    questionId: number
    rating: number | null
    choice: number | null
    text: string | null
    nps: number | null
}

// xlsx через fetch: RTK Query не отдаёт blob; без targetId - весь опрос
export const downloadSurveyExcel = async (surveyId: number, targetId?: number | null) => {
    const res = await fetch(`${BFF_SERVICE}/surveys/${surveyId}/export.xlsx${targetId ? `?targetId=${targetId}` : ''}`, {
        headers: { authorization: `Bearer ${storage.get('token')}` },
    })
    if (!res.ok) throw new Error(String(res.status))
    const url = URL.createObjectURL(await res.blob())
    const a = document.createElement('a')
    a.href = url
    a.download = `survey-${surveyId}${targetId ? `-${targetId}` : ''}.xlsx`
    a.click()
    URL.revokeObjectURL(url)
}

export const surveyApi = directorApi.enhanceEndpoints({ addTagTypes: ['Survey', 'SurveyResults', 'MySurvey'] }).injectEndpoints({
    endpoints: (build) => ({
        getSurveys: build.query<{ surveys: Survey[]; canEdit: boolean }, { type?: SurveyType } | void>({
            query: (a) => {
                const type = (a as { type?: SurveyType } | undefined)?.type
                return { url: 'surveys', params: type ? { type } : undefined }
            },
            transformResponse: (r: { data: { surveys: Survey[]; canEdit: boolean } }) => r.data,
            providesTags: ['Survey'],
        }),
        getSurveyOptions: build.query<SurveyOptions, void>({
            query: () => ({ url: 'surveys/options' }),
            transformResponse: (r: { data: SurveyOptions }) => r.data,
        }),
        // не saveSurvey: это имя уже занято в directorApi (балл опроса в зарплату), и RTK молча оставлял его
        saveStudentSurvey: build.mutation<Survey, { id: number | null; body: SurveyBody }>({
            query: ({ id, body }) => ({ url: id ? `surveys/${id}` : 'surveys', method: id ? 'PUT' : 'POST', body }),
            transformResponse: (r: { data: Survey }) => r.data,
            invalidatesTags: ['Survey', 'SurveyResults'],
        }),
        // имена с Student: в directorApi уже есть saveSurvey, совпадение молча оставляет старый endpoint
        reopenStudentSurvey: build.mutation<Survey, { id: number; openDate: string; closeDate: string }>({
            query: ({ id, ...body }) => ({ url: `surveys/${id}/reopen`, method: 'PUT', body }),
            transformResponse: (r: { data: Survey }) => r.data,
            invalidatesTags: ['Survey', 'SurveyResults'],
        }),
        deleteStudentSurvey: build.mutation<Survey, number>({
            query: (id) => ({ url: `surveys/${id}`, method: 'DELETE' }),
            transformResponse: (r: { data: Survey }) => r.data,
            invalidatesTags: ['Survey', 'SurveyResults'],
        }),
        restoreStudentSurvey: build.mutation<Survey, number>({
            query: (id) => ({ url: `surveys/${id}/restore`, method: 'POST' }),
            transformResponse: (r: { data: Survey }) => r.data,
            invalidatesTags: ['Survey', 'SurveyResults'],
        }),
        getSurveyResults: build.query<SurveyResults, number>({
            query: (id) => ({ url: `surveys/${id}/results` }),
            transformResponse: (r: { data: SurveyResults }) => r.data,
            providesTags: (_r, _e, id) => [{ type: 'SurveyResults', id }],
        }),
        applySurvey: build.mutation<
            { mentorUserId: number; courseSubjectId: number; cycleNumber: number; percent: number },
            { surveyId: number; mentorUserId: number; courseSubjectId: number }
        >({
            query: ({ surveyId, ...body }) => ({ url: `surveys/${surveyId}/results/apply`, method: 'POST', body }),
            transformResponse: (r: { data: any }) => r.data,
            invalidatesTags: (_r, _e, a) => [{ type: 'SurveyResults', id: a.surveyId }, 'Salary'],
        }),
        getCuratorSurveys: build.query<MySurveyItem[], void>({
            query: () => ({ url: 'mentor/surveys' }),
            transformResponse: (r: { data: MySurveyItem[] }) => r.data,
            providesTags: ['MySurvey'],
        }),
        getMentorSurveyStatus: build.query<MentorSurveyStatus[], void>({
            query: () => ({ url: 'mentor/surveys/students' }),
            transformResponse: (r: { data: MentorSurveyStatus[] }) => r.data,
        }),
        getCuratorSurvey: build.query<MySurvey, { surveyId: number; mentorUserId: number }>({
            query: ({ surveyId, mentorUserId }) => ({ url: `mentor/surveys/${surveyId}`, params: { mentorUserId } }),
            transformResponse: (r: { data: MySurvey }) => r.data,
            providesTags: ['MySurvey'],
        }),
        submitCuratorSurvey: build.mutation<MySurvey, { surveyId: number; mentorUserId: number; answers: SurveyAnswerBody[] }>({
            query: ({ surveyId, ...body }) => ({ url: `mentor/surveys/${surveyId}/responses`, method: 'POST', body }),
            transformResponse: (r: { data: MySurvey }) => r.data,
            invalidatesTags: ['MySurvey'],
        }),
    }),
})
